import type { ReactNode } from 'react';
import type { MenteeChecklistStep } from './MenteePortalService';
import { Link } from 'react-router-dom';

type Props = {
    step1: MenteeChecklistStep;
    step2: MenteeChecklistStep;
    step3: MenteeChecklistStep;
    onOpenThread: () => void;
};

const StepRow = ({
    title,
    rowIcon,
    stateLabel,
    action,
}: {
    title: string;
    rowIcon: string;
    stateLabel: string;
    action: ReactNode;
}) => (
    <div className="list-group-item d-flex align-items-center gap-3 py-3 px-4 bg-light bg-opacity-50 opacity-75">
        <i className={`bi ${rowIcon} fs-5`}></i>
        <div className="flex-grow-1">
            <div className="fw-bold text-dark font-14">{title}</div>
            <p className="mdash-check-state">{stateLabel}</p>
        </div>
       
        {action}
    </div>
);

/** Ticket texts verbatim for the three steps. */
const MenteeDashboardChecklist = ({ step1, step2, step3, onOpenThread }: Props) => {
    const step1Done = step1.state === 'done';
    const step2Done = step2.state === 'done';
    const step2Start = step2.state === 'start_conversation';
    const step2Reply = step2.state === 'reply_now';
    const bookEnabled = Boolean(step3.bookEnabled);

    return (
        <section className="card border-0 shadow-sm rounded-3 overflow-hidden bg-white mb-3">
            <div className="card-header bg-primary bg-gradient border-0 text-white p-3">
                <div className="d-flex align-items-center justify-content-between">
                  <h6 className="fw-bold mb-0">🚀 Getting Started — 1 of 3 steps done</h6>
                  <span className="small text-white-50">Take these steps to make the most of your mentorship</span>
                </div>
              </div>
            <div className="progress" role="progressbar" aria-label="Basic example" aria-valuenow={25} aria-valuemin={0} aria-valuemax={100} style={{ height: '5px' }}>
                <div className="progress-bar bg-warning" style={{ width: '33%' }}></div>
            </div>

            <div className="list-group list-group-flush">
                <StepRow
                    title="Complete your Mentee Registration Form"
                    rowIcon="bi-check-circle-fill text-success"
                    stateLabel={step1Done ? 'Done' : 'Not complete'}
                    action={
                        step1Done ? (
                            <span className="badge bg-success bg-opacity-10 text-success fw-bold py-1 px-3 font-12 lh-base rounded-pill">Done</span>
                        ) : (
                            <Link className="btn btn-sm btn-primary rounded-pill" to="/alumni/mentorship/mentee/register">
                                Complete Now
                            </Link>
                        )
                    }
                />
                <StepRow
                    title={
                        step2Start
                            ? 'Start a conversation with your mentor'
                            : "Read your mentor's messages"
                    }
                    rowIcon="bi-chat-fill text-primary"
                    stateLabel={
                        step2Done ? 'Done' : step2Start ? 'No messages yet' : 'Reply needed'
                    }
                    action={
                        step2Done ? (
                            <span className="badge bg-success bg-opacity-10 text-success fw-bold py-1 px-3 font-12 lh-base rounded-pill">Done</span>
                        ) : step2Reply ? (
                            <button type="button" className="btn btn-sm btn-primary rounded-pill" onClick={onOpenThread}>
                                Reply Now
                            </button>
                        ) : (
                            <button type="button" className="btn btn-sm btn-primary rounded-pill" onClick={onOpenThread}>
                                Send message
                            </button>
                        )
                    }
                />
                <StepRow
                    title="Book your first 1:1 session"
                    rowIcon="bi-calendar-event text-secondary"
                    stateLabel={
                        bookEnabled
                            ? 'Mentor has availability'
                            : 'Available when your mentor sets availability'
                    }
                    action={
                        bookEnabled ? (
                            <Link
                                className="btn btn-sm btn-primary rounded-pill px-3"
                                to="/alumni/mentorship/mentee/sessions?tab=book"
                            >
                                Book Now
                            </Link>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-sm btn-primary rounded-pill px-3"
                                disabled
                                title="Available when your mentor sets availability"
                            >
                                Book Now
                            </button>
                        )
                    }
                />
            </div>
        </section>
    );
};

export default MenteeDashboardChecklist;
